/* eslint-disable max-len */
/**
 * @fileoverview Centralized user-facing text for the content workflow.
 *               Every heading, subtitle, placeholder, and guidance string lives here
 *               so changes propagate across all steps from one place.
 *               PATH: src/components/shared/constants/workflowCopy.js
 */

// ---------------------------------------------------------------------------
// STEP TITLES & SUBTITLES
// ---------------------------------------------------------------------------

export const STEP_TITLES = {
    MISSION_BRIEFING: "Let's Create Something",
    SPARK: 'The Spark',
    CLARIFICATION: 'Quick Questions',
    FINE_TUNE: 'Brief Review',
    PLAN: 'The Plan',
    PREVIEW: 'Preview & Edit',
    VISUALS: 'Visual Studio',
    PUBLISH: 'Publish',
};

export const STEP_SUBTITLES = {
    MISSION_BRIEFING: 'Pick your format, choose your platforms, and decide if this is a one-off or a series.',
    SPARK: 'Describe your idea and the AI will take it from there.',
    CLARIFICATION: 'A few quick questions to help the AI write better content for you.',
    FINE_TUNE: 'Review what the AI understood. Edit if needed, then continue.',
    PLAN: 'Review the content plan the AI created for your series.',
    PREVIEW: 'Review, edit, and polish your content before moving to visuals.',
    VISUALS: 'Generate or upload visuals for each post.',
    PUBLISH: 'Set a date, pick a time, and send your content into the world.',
};

// ---------------------------------------------------------------------------
// STEP DEFINITIONS (used by WorkflowStepper)
// ---------------------------------------------------------------------------

/**
 * Ordered list of all possible workflow steps with display metadata.
 * The `key` matches the internal step identifiers used in ContentWorkflow.
 * `streamOnly` steps are hidden from the stepper when the workflow is 'single'.
 */
export const WORKFLOW_STEP_LIST = [
    { key: 'mission_briefing', number: 1, label: STEP_TITLES.MISSION_BRIEFING, streamOnly: false },
    { key: 'spark', number: 2, label: STEP_TITLES.SPARK, streamOnly: false },
    { key: 'clarification', number: 3, label: STEP_TITLES.CLARIFICATION, streamOnly: true },
    { key: 'fine_tune', number: 4, label: STEP_TITLES.FINE_TUNE, streamOnly: false },
    { key: 'plan', number: 5, label: STEP_TITLES.PLAN, streamOnly: true },
    { key: 'preview', number: 6, label: STEP_TITLES.PREVIEW, streamOnly: false },
    { key: 'visuals', number: 7, label: STEP_TITLES.VISUALS, streamOnly: false },
    { key: 'publish', number: 8, label: STEP_TITLES.PUBLISH, streamOnly: false },
];

// ---------------------------------------------------------------------------
// FORMAT-SPECIFIC PLACEHOLDERS
// ---------------------------------------------------------------------------

export const FORMAT_PLACEHOLDERS = {
    standard: 'e.g., "A post about the top 3 productivity hacks for remote workers..."',
    carousel: 'e.g., "A step-by-step guide to meal prepping — the AI will break it into slides..."',
    reel: 'e.g., "A quick 30-second tip about morning routines that actually work..."',
    story_image: 'e.g., "A behind-the-scenes look at our product launch..."',
    story_video: 'e.g., "A quick teaser for our upcoming event with countdown energy..."',
};

// ---------------------------------------------------------------------------
// FORMAT-SPECIFIC GUIDANCE
// ---------------------------------------------------------------------------

export const FORMAT_GUIDANCE = {
    standard: 'Describe what you want to post about. The AI will craft the perfect caption.',
    carousel: 'Describe the topic for your carousel. The AI will turn it into a swipeable slide deck.',
    reel: "What should the reel be about? Think short, punchy, and visually driven.",
    story_image: "What's the story about? Keep it timely — stories disappear after 24 hours.",
    story_video: "What's the story about? Keep it timely — stories disappear after 24 hours.",
};

// ---------------------------------------------------------------------------
// MISSION BRIEFING (Step 1) COPY
// ---------------------------------------------------------------------------

export const MISSION_BRIEFING_COPY = {
    header: STEP_TITLES.MISSION_BRIEFING,
    subheader: STEP_SUBTITLES.MISSION_BRIEFING,
    formatSectionTitle: 'What are you making?',
    platformSectionTitle: 'Where should it go?',
    workflowSectionTitle: 'One post or a whole series?',
    submitButton: 'Start Creating',
    carouselStreamNote: 'Carousels work best as standalone posts.',
    platformNotConnected: (platform) =>
        `No ${platform === 'facebook' ? 'Facebook Page' : 'Instagram Business Account'} connected. Link it in Project Settings.`,
    platformMinimumWarning: 'Please select at least one platform.',
};

// ---------------------------------------------------------------------------
// FINE-TUNE (Step 3) COPY
// ---------------------------------------------------------------------------

export const FINE_TUNE_COPY = {
    header: STEP_TITLES.FINE_TUNE,
    subheader: STEP_SUBTITLES.FINE_TUNE,
    briefLabel: 'Creative Brief',
    briefHelper: 'This brief guides all AI-generated content and visuals. Edit it to steer the output.',
    submitButton: 'Continue',
};

// ---------------------------------------------------------------------------
// PUBLISH (Step 7) COPY — consistent naming
// ---------------------------------------------------------------------------

export const PUBLISH_COPY = {
    header: STEP_TITLES.PUBLISH,
    subheader: STEP_SUBTITLES.PUBLISH,
    submitButton: 'Publish',
    scheduleButton: 'Schedule',
    postNowButton: 'Post Now',
};
